import { BaSeErrorPage, BaSeLoading } from '@base/react';
import * as Sebrae from '@sebrae/react-core';
import * as React from 'react';
import {
  GestaoAmeiFrameHeightChangeEventPayload,
  GestaoAmeiNotificationEventPayload,
  GestaoAmeiProps,
  GestaoAmeiSaveUserRolesEventPayload,
} from './props';
import { QueryString, useUrl } from './url';
import { validation } from './validation';

const title = 'Gestão AMEI';
const subscriptionKeyNotification = '×notification×gestao-amei×';
const subscriptionKeySaveUser = '×save-user-roles×gestao-amei×';
const subscriptionKeyResizeFrame = '×resize-frame×gestao-amei×';
const initialFrameStyle = {
  width: '100%',
  height: '100%',
  border: 'none',
  outline: 'none',
};

const moduleOptionsUrlMapper = {
  // Module: role-map
  'role-map': {
    entrypoint: ['clientId', 'editUser'],
    entrypointReplacer: {
      usersSuggestion: 'new-user',
    },
    queryString: ['rolesSuggestion', 'usersSuggestion'],
    queryStringReplacer: {
      usersSuggestion: 'dontGoBack=true',
      editUser: 'dontGoBack=true',
    },
  },
  // Module: ...
};

export const GestaoAmeiFrame: React.FC<GestaoAmeiProps> = ({
  realm,
  url,
  module,
  height = 'auto',
  width = 'auto',
  theme = {},
  events,
  // Module: role-map
  roleMapOptions,
  roleMapEvents,
  // Module: ...
}) => {
  validation({
    realm,
    height,
    width,
    theme,
    events,
    module,

    // Module: role-map
    roleMapOptions,
    roleMapEvents,
    // Module: ...
  });

  const { subscribe } = Sebrae.useEventBus();

  const { initialized, entrypoint, mountUrl } = useUrl(url);

  const [error, setError] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [frameHeight, setFrameHeight] = React.useState(height);

  const moduleOptions: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
  } = React.useMemo(
    () => ({
      // Module: role-map
      ...roleMapOptions,
      // Module: ...
    }),
    [roleMapOptions],
  );

  const src = React.useMemo(() => {
    if (initialized && module) {
      const paths = ([realm, module] as string[])
        .concat(
          moduleOptionsUrlMapper[module].entrypoint.map(
            (key) => moduleOptions?.[key]?.toString?.() ?? '',
          ),
        )
        .concat(
          Object.entries(moduleOptionsUrlMapper[module].entrypointReplacer).map(
            ([key, value]) => (key in moduleOptions ? value : ''),
          ),
        );
      const endpoint = `${entrypoint(paths)}?embed`;

      const queryString: QueryString = { ...theme };
      moduleOptionsUrlMapper[module].queryString.forEach((key) => {
        const value: string = moduleOptions?.[key]?.toString?.() ?? '';
        if (value) {
          queryString[key] = value;
        }
      });
      Object.entries(
        moduleOptionsUrlMapper[module].queryStringReplacer,
      ).forEach(([key, value]) => {
        if (key in moduleOptions) {
          const [queryStringKey, queryStringValue] = value.split('=');
          queryString[queryStringKey] = queryStringValue;
        }
      });

      return mountUrl(endpoint, queryString);
    }
    return undefined;
  }, [
    initialized,
    moduleOptions,
    entrypoint,
    mountUrl,
    realm,
    module,
    roleMapOptions,
    theme,
  ]);

  const createNotificationSubscription = React.useCallback(
    () =>
      subscribe(
        subscriptionKeyNotification,
        (payload?: GestaoAmeiNotificationEventPayload) => {
          if (payload) {
            events?.onNotification?.(payload);
          }
        },
      ),
    [events, subscribe],
  );

  const createSaveUserRolesSubscription = React.useCallback(
    () =>
      subscribe(
        subscriptionKeySaveUser,
        (payload?: GestaoAmeiSaveUserRolesEventPayload) => {
          if (payload) {
            roleMapEvents?.onSaveUserRoles?.(payload);
          }
        },
      ),
    [roleMapEvents, subscribe],
  );

  const createResizeFrameSubscription = React.useCallback(
    () =>
      subscribe(
        subscriptionKeyResizeFrame,
        (payload?: GestaoAmeiFrameHeightChangeEventPayload) => {
          if (payload && height === 'auto') {
            setFrameHeight(payload.height);
          }
        },
      ),
    [height, subscribe],
  );

  React.useEffect(() => {
    const notificationSubscription = createNotificationSubscription();
    const saveUserRolesSubscription = createSaveUserRolesSubscription();
    const resizeFrameSubscription = createResizeFrameSubscription();

    return () => {
      notificationSubscription.unsubscribe();
      saveUserRolesSubscription.unsubscribe();
      resizeFrameSubscription.unsubscribe();
    };
  }, [
    createNotificationSubscription,
    createResizeFrameSubscription,
    createSaveUserRolesSubscription,
  ]);

  if (error) {
    return <BaSeErrorPage title={`Erro ao carregar o ${title}`} />;
  }

  return (
    <section style={{ height: frameHeight, width }}>
      {loading && <BaSeLoading />}
      <iframe
        title={title}
        src={src}
        allow=""
        onError={() => setError(true)}
        onLoad={() => setLoading(false)}
        style={{
          ...initialFrameStyle,
          display: loading ? 'none' : 'block',
        }}
      />
    </section>
  );
};

GestaoAmeiFrame.displayName = 'GestaoAmeiFrame';
